import toastr from 'toastr';
import uuidv4 from 'uuid/v4';
import { MFChannel } from '../../Components/SDK';
import { getAppOnlineStatus, getChannelInfo, setActiveConversationChannel, setUserInfoArr } from '../../Slice';
import { toggleCreatingChannel } from '../../Slice/channelInfoSlice';
import { setActiveLeftPane } from '../../Slice/leftPaneNavSlice';
import Store from '../../Store';
import {
   ChatHelpers,
   ChatMessageHistoryDataAction,
   MessageAction,
   RecentChatUpdateAction,
   Utility,
} from '../rootAccess';
import { resetActionModal } from './actionModalUtils';
import { getLocalUserDetails } from './commonActions';
import { NO_INTERNET } from './constants';
import { showSucessToast } from './toast';

let isCreating = false; // local flag to prevent re-entry

export const handleChannelNavigation = ({ pane, params }) => {
   Store.dispatch(setActiveLeftPane({ activeLeftPane: pane, params }));
};

export const handleCreateChannel = async createChannelObj => {
   if (isCreating) return;

   if (!getAppOnlineStatus()) return toastr.error(NO_INTERNET);

   isCreating = true;
   Store.dispatch(toggleCreatingChannel());

   try {
      const res = await MFChannel.createChannel(createChannelObj);
      const { statusCode, data, data: { channelId } = {}, message } = res;

      if (statusCode !== 200) {
         toastr.error(message);
      } else {
         toastr.success('Channel created successfully');
         handleChannelNavigation({ pane: 'channelUserList', params: { channelId } }); // Navigate to user selection
         Store.dispatch(setActiveConversationChannel(data));
      }
   } catch (error) {
      toastr.error('Something went wrong. Please try again.');
      console.error(error);
   } finally {
      Store.dispatch(toggleCreatingChannel());
      isCreating = false;
   }
};

export const handleAddMembersToChannel = async (channelId, userListScopeKey) => {
   if (!getAppOnlineStatus()) return toastr.error(NO_INTERNET);
   const participants = Store.getState().channelInfoData.userListSelection?.[userListScopeKey];
   const res = await MFChannel.addMembersToChannel({ channelId, participants });
   const { statusCode, message } = res;
   if (statusCode === 200) {
      handleChannelNavigation({ pane: 'recentChannelList' });
      resetActionModal();
      showSucessToast(participants.length > 1 ? 'Members added successfully' : 'Member added successfully');
   } else {
      toastr.error(message);
   }
};

export const fetchChannelMembers = async ({ channelId, page = 1, size = 20, search = '' }) => {
   try {
      const response = await MFChannel.fetchChannelMembers({ channelId, page, size, search });
      const { statusCode, data: { members = [], totalMembers = 0 } = {} } = response;
      if (statusCode !== 200) {
         return { members: [], totalMembers: 0 };
      }
      Store.dispatch(setUserInfoArr(members));
      return { members, totalMembers };
   } catch (error) {
      return [];
   }
};

export const handleInviteMembers = async (channelId, userListScopeKey) => {
   try {
      if (!getAppOnlineStatus()) return toastr.error(NO_INTERNET);

      const participants = Store.getState().channelInfoData.userListSelection?.[userListScopeKey];
      const channelInfoDetails = getChannelInfo(channelId);

      const userInvites = participants
         .filter(p => p?.userId)
         .map(p => ({
            toJid: ChatHelpers.formatUserIdToJid(p.userId),
            msgId: uuidv4(),
         }));

      const channelInfo = {
         channelId: channelId,
         name: channelInfoDetails.name,
         imageUri: channelInfoDetails.imageUri,
         accessMode: channelInfoDetails.settings.accessMode,
         messageMode: channelInfoDetails.settings.messageMode,
         inviteLink: channelInfoDetails.accessUri,
      };
      const response = await MFChannel.inviteUsersToChannel({ userInvites, channelInfo });
      await Promise.all(
         userInvites.map(async element => {
            const dataObj = {
               jid: element.toJid,
               msgType: 'channelInvitation',
               message: channelInfo.inviteLink,
               channelInvitation: channelInfo,
               chatType: 'chat',
               userProfile: { data: getLocalUserDetails() },
               msgId: element.msgId,
            };

            const conversationChatObj = await Utility.getMessageObjSender(dataObj);
            const recentChatObj = Utility.getRecentChatMsgObj(dataObj);
            const dispatchData = { data: [conversationChatObj], userJid: element.toJid };
            Store.dispatch(MessageAction(conversationChatObj));
            Store.dispatch(ChatMessageHistoryDataAction(dispatchData));
            Store.dispatch(RecentChatUpdateAction(recentChatObj));
         }),
      );
      if (response.statusCode === 200) {
         handleChannelNavigation({ pane: 'recentChannelList' });
         resetActionModal();
         showSucessToast('Invite member successfully');
      }
   } catch (error) {
      return [];
   }
};
